import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  buildCbcLineData,
  buildLipidBarData,
  buildRadarFromReports,
  formatChartTickLabel,
  rowsHaveAnyMetrics,
  withChartTickKeys
} from "../chartDataFromReports";
import { BLOOD_COUNT_TREND, CHART_COLORS, LIPID_COMPARISON, RADAR_HEALTH } from "../data";
import ChartShell from "./ChartShell";

const tooltipStyle = {
  contentStyle: {
    borderRadius: 12,
    border: "1px solid rgb(226 232 240)",
    boxShadow: "0 4px 12px rgba(15,23,42,0.08)"
  },
  labelStyle: { fontWeight: 600, color: "#0f172a" }
};

function EmptyChart({ message }) {
  return (
    <div className="flex h-64 items-center justify-center px-4 text-center text-sm text-slate-500 sm:h-72">
      {message}
    </div>
  );
}

function EmptyRadar({ message }) {
  return (
    <div className="flex h-72 items-center justify-center px-4 text-center text-sm text-slate-500 sm:h-80">
      {message}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} [props.reports] MediTrack rows (with optional `metrics` from API).
 * @param {boolean} [props.useDemoData=false] When true and live metrics are missing, use bundled demo series.
 */
export default function CompareCharts({ reports = [], useDemoData = false }) {
  const live = rowsHaveAnyMetrics(reports);

  const bloodData = useMemo(() => {
    const fromReports = buildCbcLineData(reports);
    const base = fromReports.length ? fromReports : useDemoData ? BLOOD_COUNT_TREND : [];
    return withChartTickKeys(base, "demo-blood");
  }, [reports, useDemoData]);

  const lipidData = useMemo(() => {
    const fromReports = buildLipidBarData(reports);
    const base = fromReports.length ? fromReports : useDemoData ? LIPID_COMPARISON : [];
    return withChartTickKeys(base, "demo-lipid");
  }, [reports, useDemoData]);

  const radarData = useMemo(() => {
    const fromReports = buildRadarFromReports(reports);
    if (fromReports.length) return fromReports;
    return useDemoData ? RADAR_HEALTH : [];
  }, [reports, useDemoData]);

  const bloodSubtitle = live
    ? "WBC, RBC, and hemoglobin from your uploaded reports (oldest → newest)."
    : "WBC, RBC, and hemoglobin over the last six months.";
  const lipidSubtitle = live
    ? "LDL, HDL, and triglycerides from your lipid reports by test date."
    : "LDL, HDL, and triglycerides by test date.";
  const radarSubtitle = live
    ? "Domain scores derived from your latest reports vs the prior report on file."
    : "Relative standing vs ideal (100 = optimal) — current vs six months ago.";

  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
      <ChartShell title="Blood count trends" subtitle={bloodSubtitle}>
        {bloodData.length === 0 ? (
          <EmptyChart message="Upload reports that include WBC, RBC, or hemoglobin to see this chart." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <LineChart data={bloodData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(bloodData, tk)}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(bloodData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="wbc"
                  name="WBC (×10³)"
                  stroke={CHART_COLORS.primary}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="rbc"
                  name="RBC (mill/uL)"
                  stroke={CHART_COLORS.indigo}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="hgb"
                  name="Hgb (g/dL)"
                  stroke={CHART_COLORS.success}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      <ChartShell title="Lipid profile comparison" subtitle={lipidSubtitle}>
        {lipidData.length === 0 ? (
          <EmptyChart message="Upload reports that include LDL, HDL, or triglycerides to see this chart." />
        ) : (
          <div className="h-64 w-full min-h-[14rem] min-w-0 sm:h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <BarChart data={lipidData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="tickKey"
                  type="category"
                  tickFormatter={(tk) => formatChartTickLabel(lipidData, tk)}
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  label={{ value: "mg/dL", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
                />
                <Tooltip {...tooltipStyle} labelFormatter={(label) => formatChartTickLabel(lipidData, label)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ldl" name="LDL" fill={CHART_COLORS.danger} radius={[6, 6, 0, 0]} maxBarSize={36} />
                <Bar dataKey="hdl" name="HDL" fill={CHART_COLORS.success} radius={[6, 6, 0, 0]} maxBarSize={36} />
                <Bar dataKey="trig" name="Triglycerides" fill={CHART_COLORS.warning} radius={[6, 6, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>

      <ChartShell title="Multi-marker radar" subtitle={radarSubtitle} className="lg:col-span-2">
        {radarData.length === 0 ? (
          <EmptyRadar message="Upload at least one report with lab values to see your multi-domain profile." />
        ) : (
          <div className="h-72 w-full min-h-[16rem] min-w-0 sm:h-80 md:h-96">
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <RadarChart data={radarData} margin={{ top: 16, right: 24, bottom: 16, left: 24 }}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="domain" tick={{ fill: "#64748b", fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                <Radar
                  name="Current"
                  dataKey="current"
                  stroke={CHART_COLORS.primary}
                  fill={CHART_COLORS.primary}
                  fillOpacity={0.35}
                  strokeWidth={2}
                />
                <Radar
                  name="Previous"
                  dataKey="previous"
                  stroke={CHART_COLORS.secondary}
                  fill={CHART_COLORS.secondary}
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip {...tooltipStyle} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartShell>
    </div>
  );
}
